import dayjs from "dayjs";
import React, { FC, useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useGetUsersDetailByRoleMutation } from "../../../../../../../../api/services/user/userApi";
import {
  FormController,
  FormControllerCheckbox,
  MuiBox,
  MuiCheckbox,
  MuiDateFieldFormController,
  MuiGrid,
  MuiSelect,
  OptionsDataType,
} from "../../../../../../../../components";
import {
  ROLE_IDS,
  SOLUTION_OFFERING_CMC_SITECORE_KEY,
  TM_INDIVIDUAL_ASSESSMENT,
  TM_LEADER_COACHING,
} from "../../../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import {
  getTodayDate,
  isNotEmpty,
  mapDropdownDataWithName,
} from "../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../../../../redux/slices/appState/appStateSelector";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../../../redux/slices/appState/appStateSlice";
import {
  ClientManaged,
  OnsiteNotification,
  OutreachEligible,
  ParticipantTabFormKey,
  ParticipantTabFormType,
  PrimaryManagedStatusKeys,
  ProgramDetail,
  VirtualNotification,
} from "../../../../../../modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";

interface ParticipantOutreachFormProps {
  programData?: ProgramDetail;
  pageId: string;
  isEditProgram?: boolean;
  editParticipantData?: ParticipantTabFormType;
  isFromCoachPortal?: boolean;
}

export const ParticipantOutreach: FC<ParticipantOutreachFormProps> = ({
  programData,
  pageId,
  isEditProgram,
  editParticipantData,
  isFromCoachPortal = false,
}) => {
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();
  const {
    control,
    setValue,
    getValues,
    watch,
    clearErrors,
    trigger,
    formState: { dirtyFields },
  } = useFormContext();
  const { fetchLabel, commonLabels, fetchDropdownValues } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const dispatch = useDispatch();
  const [initialAvoidDone, setInitialAvoidDone] =
    React.useState<boolean>(false);

  const [secondaryManagedStatusList, setSecondaryManagedStatusList] =
    React.useState<OptionsDataType[]>([]);

  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  const todayStr = getTodayDate();

  const outreachEligibleDateWatch = useWatch({
    control,
    name: ParticipantTabFormKey.outreachEligibleDate,
  });

  const notificationSupportStatusWatch = useWatch({
    control,
    name: ParticipantTabFormKey.notificationSupportStatus,
  });

  const getLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.outreach, key);
  };

  const getDropdownValues = (key: string) => {
    return fetchDropdownValues(programSiteCoreKeys.outreach, key);
  };

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const {
    mutateAsync: getAssignedOutreachConsultant,
    data: assignedOutreachConsultantList,
    isPending: isAssignedOutreachConsultantPending,
    isError: isAssignedOutreachConsultantError,
  } = useGetUsersDetailByRoleMutation();

  useEffect(() => {
    if (isNotEmpty(programData)) {
      getAssignedOutreachConsultant({
        roleIds: [
          ROLE_IDS.outreachConsultantLead,
          ROLE_IDS.outreachTeam,
        ],
      });
    }
  }, [programData]);

  const isNotificationSupportDateMandatory = useMemo(
    () =>
      [VirtualNotification, OnsiteNotification].includes(
        notificationSupportStatusWatch,
      ),
    [notificationSupportStatusWatch],
  );

  useEffect(() => {
    dispatch(
      toggleLoader({
        outreachConsultantPending: {
          isVisible: isAssignedOutreachConsultantPending,
          message: commonLabels.loadingPleaseWait,
        },
      }),
    );
    return () => {
      dispatch(
        toggleLoader({ outreachConsultantPending: { isVisible: false } }),
      );
    };
  }, [isAssignedOutreachConsultantPending]);

  useEffect(() => {
    if (isAssignedOutreachConsultantError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isAssignedOutreachConsultantError]);

  useEffect(() => {
    if (isEditProgram && !editParticipantData) {
      return;
    }
    const eligibility = getValues(ParticipantTabFormKey.eligibility);
    if (
      eligibility &&
      isEditProgram &&
      !initialAvoidDone &&
      eligibility === editParticipantData?.eligibility
    ) {
      setInitialAvoidDone(true);
      return;
    }
    setValue(ParticipantTabFormKey.outreachEligibleStatus, "");
    setValue(ParticipantTabFormKey.assignedOutreachConsultant, "");
    setValue(ParticipantTabFormKey.outreachEligibleDate, "");
    setValue(ParticipantTabFormKey.primaryManagedStatus, "");
    setValue(ParticipantTabFormKey.secondaryManagedStatus, "");
    setValue(ParticipantTabFormKey.contactWithin24Hours, false);

    clearErrors(ParticipantTabFormKey.outreachEligibleStatus);
    clearErrors(ParticipantTabFormKey.assignedOutreachConsultant);
    clearErrors(ParticipantTabFormKey.outreachEligibleDate);
    clearErrors(ParticipantTabFormKey.primaryManagedStatus);
    clearErrors(ParticipantTabFormKey.secondaryManagedStatus);
  }, [watch(ParticipantTabFormKey.eligibility)]);

  useEffect(() => {
    // will manually check and throw error
    if (
      dirtyFields?.outreachEligibleDate &&
      isNotEmpty(getValues(ParticipantTabFormKey.notificationDate))
    ) {
      trigger(ParticipantTabFormKey.notificationDate);
    }
  }, [outreachEligibleDateWatch]);

  const isOutreachEligible = useMemo(() => {
    return getValues(ParticipantTabFormKey.eligibility) === OutreachEligible;
  }, [watch(ParticipantTabFormKey.eligibility)]);

  const isClientManaged = useMemo(() => {
    return getValues(ParticipantTabFormKey.eligibility) === ClientManaged;
  }, [watch(ParticipantTabFormKey.eligibility)]);

  const updateSecondaryManagedStatusList = () => {
    const primaryStatus = getValues(
      ParticipantTabFormKey.primaryManagedStatus,
    ) as keyof typeof statusMapping;

    if (!primaryStatus) return;

    const statusMapping = {
      [PrimaryManagedStatusKeys.ClientManagedNoOutreachPermitted]:
        programSiteCoreKeys.noOutreachPermittedList,
      [PrimaryManagedStatusKeys.ClientManagedPendingFurtherActions]:
        programSiteCoreKeys.pendingFurtherActionsList,
      [PrimaryManagedStatusKeys.ClientManagedPendingORE]:
        programSiteCoreKeys.pendingOREList,
    };

    setSecondaryManagedStatusList(
      statusMapping[primaryStatus]
        ? getDropdownValues(statusMapping[primaryStatus])
        : [],
    );
  };

  useEffect(() => {
    const primaryStatus = getValues(ParticipantTabFormKey.primaryManagedStatus);
    if (!siteCoreLoading && primaryStatus) updateSecondaryManagedStatusList();
  }, [watch(ParticipantTabFormKey.primaryManagedStatus), siteCoreLoading]);

  const isCMCProgram = useMemo(() => {
    return (
      programData?.solutionOfferingSiteCoreKey ===
      SOLUTION_OFFERING_CMC_SITECORE_KEY
    );
  }, [programData?.solutionOfferingSiteCoreKey]);

  const isTMAssessmentProgram = useMemo(() => {
    return programData?.tmProgramType === TM_INDIVIDUAL_ASSESSMENT;
  }, [programData?.tmProgramType]);

  const isTMLeaderCoachingProgram = useMemo(() => {
    return programData?.tmProgramType === TM_LEADER_COACHING;
  }, [programData?.tmProgramType]);

  return (
    <MuiBox className="participant-outreach" data-testid="participant-outreach">
      <MuiGrid container className="main-container">
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.eligibility}
          >
            <MuiSelect
              label={getLabel(programSiteCoreKeys.eligibility)}
              options={getDropdownValues(programSiteCoreKeys.eligibilityList)}
              visibleDefaultSelectOption
              value=""
              isMandatory
              id={`${pageId}-${ParticipantTabFormKey.eligibility}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
              disabled={isFromCoachPortal}
            />
          </FormController>
        </MuiGrid>

        {isOutreachEligible && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ParticipantTabFormKey.outreachEligibleStatus}
            >
              <MuiSelect
                label={getLabel(programSiteCoreKeys.outreachEligibleStatus)}
                options={getDropdownValues(
                  programSiteCoreKeys.outreahEligibleStatusList,
                )}
                visibleDefaultSelectOption
                value=""
                isMandatory
                id={`${pageId}-${ParticipantTabFormKey.outreachEligibleStatus}`}
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
                disabled={isFromCoachPortal}
              />
            </FormController>
          </MuiGrid>
        )}

        {isClientManaged && (
          <>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={ParticipantTabFormKey.primaryManagedStatus}
              >
                <MuiSelect
                  label={getLabel(programSiteCoreKeys.primaryManagedStatus)}
                  options={getDropdownValues(
                    programSiteCoreKeys.primaryManagedStatusList,
                  )}
                  visibleDefaultSelectOption
                  value=""
                  isMandatory
                  id={`${pageId}-${ParticipantTabFormKey.primaryManagedStatus}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                  disabled={isFromCoachPortal}
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={ParticipantTabFormKey.secondaryManagedStatus}
              >
                <MuiSelect
                  label={getLabel(programSiteCoreKeys.secondaryManagedStatus)}
                  options={[...secondaryManagedStatusList]}
                  visibleDefaultSelectOption
                  value=""
                  id={`${pageId}-${ParticipantTabFormKey.secondaryManagedStatus}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                  disabled={isFromCoachPortal}
                />
              </FormController>
            </MuiGrid>
          </>
        )}

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.assignedOutreachConsultant}
          >
            <MuiSelect
              label={getLabel(programSiteCoreKeys.assignedOutreachConsultant)}
              options={mapDropdownDataWithName(assignedOutreachConsultantList)}
              visibleDefaultSelectOption
              value=""
              id={`${pageId}-${ParticipantTabFormKey.assignedOutreachConsultant}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
              showSearch
              disabled={isFromCoachPortal}
            />
          </FormController>
        </MuiGrid>

        {!isCMCProgram &&
          !isTMAssessmentProgram &&
          !isTMLeaderCoachingProgram && (
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDateFieldFormController
                control={control}
                name={ParticipantTabFormKey.releaseDate}
                label={getLabel(programSiteCoreKeys.releaseDate)}
                valueFormat={userCountryDateFormat}
                format={userCountryDateFormat}
                className="date-picker"
                id={`${pageId}-${ParticipantTabFormKey.releaseDate}`}
                disableManualInput
                value={
                  getValues(ParticipantTabFormKey.releaseDate) !== ""
                    ? dayjs(
                        getValues(ParticipantTabFormKey.releaseDate),
                        userCountryDateFormat,
                      )
                    : null
                }
                requiredARIALabel={commonARIALabel.required}
                selectDateARIALabel={commonARIALabel.selectDate}
                disabled={isFromCoachPortal}
              />
            </MuiGrid>
          )}

        {isOutreachEligible && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDateFieldFormController
              control={control}
              name={ParticipantTabFormKey.outreachEligibleDate}
              label={getLabel(programSiteCoreKeys.outreachEligibleDate)}
              valueFormat={userCountryDateFormat}
              format={userCountryDateFormat}
              isMandatory
              className="date-picker"
              minDate={todayStr}
              disableManualInput
              id={`${pageId}-${ParticipantTabFormKey.outreachEligibleDate}`}
              value={
                getValues(ParticipantTabFormKey.outreachEligibleDate) !== ""
                  ? dayjs(
                      getValues(ParticipantTabFormKey.outreachEligibleDate),
                      userCountryDateFormat,
                    )
                  : null
              }
              requiredARIALabel={commonARIALabel.required}
              selectDateARIALabel={commonARIALabel.selectDate}
              disabled={isFromCoachPortal}
            />
          </MuiGrid>
        )}
      </MuiGrid>

      {!isCMCProgram &&
        !isTMAssessmentProgram &&
        !isTMLeaderCoachingProgram && (
          <MuiGrid container className="section-grid-container">
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDateFieldFormController
                control={control}
                name={ParticipantTabFormKey.notificationDate}
                label={getLabel(programSiteCoreKeys.notificationDate)}
                valueFormat={userCountryDateFormat}
                format={userCountryDateFormat}
                isMandatory
                className="date-picker"
                id={`${pageId}-${ParticipantTabFormKey.notificationDate}`}
                disableManualInput
                value={
                  getValues(ParticipantTabFormKey.notificationDate) !== ""
                    ? dayjs(
                        getValues(ParticipantTabFormKey.notificationDate),
                        userCountryDateFormat,
                      )
                    : null
                }
                requiredARIALabel={commonARIALabel.required}
                selectDateARIALabel={commonARIALabel.selectDate}
                disabled={isFromCoachPortal}
              />
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={ParticipantTabFormKey.notificationSupportStatus}
              >
                <MuiSelect
                  label={getLabel(
                    programSiteCoreKeys.notificationSupportStatus,
                  )}
                  options={getDropdownValues(
                    programSiteCoreKeys.notificationSupportStatusList,
                  )}
                  isMandatory
                  visibleDefaultSelectOption
                  value=""
                  id={`${pageId}-${ParticipantTabFormKey.notificationSupportStatus}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                  disabled={isFromCoachPortal}
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDateFieldFormController
                control={control}
                name={ParticipantTabFormKey.notificationSupportDate}
                label={getLabel(programSiteCoreKeys.notificationSupportDate)}
                valueFormat={userCountryDateFormat}
                format={userCountryDateFormat}
                isMandatory={isNotificationSupportDateMandatory}
                className="date-picker"
                disableManualInput
                id={`${pageId}-${ParticipantTabFormKey.notificationSupportDate}`}
                value={
                  getValues(ParticipantTabFormKey.notificationSupportDate) !==
                  ""
                    ? dayjs(
                        getValues(
                          ParticipantTabFormKey.notificationSupportDate,
                        ),
                        userCountryDateFormat,
                      )
                    : null
                }
                requiredARIALabel={commonARIALabel.required}
                selectDateARIALabel={commonARIALabel.selectDate}
                disabled={isFromCoachPortal}
              />
            </MuiGrid>
          </MuiGrid>
        )}

      {!isCMCProgram &&
        !isTMAssessmentProgram &&
        !isTMLeaderCoachingProgram && (
          <MuiGrid container className="section-grid-container">
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormControllerCheckbox
                control={control}
                name={ParticipantTabFormKey.contactWithin24Hours}
              >
                <MuiCheckbox
                  checkboxLabel={getLabel(
                    programSiteCoreKeys.contactWithin24Hours,
                  )}
                  id={`${pageId}-${ParticipantTabFormKey.contactWithin24Hours}`}
                  data-testid={`${pageId}-${ParticipantTabFormKey.contactWithin24Hours}`}
                  disabled={isFromCoachPortal}
                />
              </FormControllerCheckbox>
            </MuiGrid>
          </MuiGrid>
        )}
    </MuiBox>
  );
};
